import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from '@grubhubprod/umami-library/dist/nodash/debounce';
import { DropdownOptionProps } from 'src/components/CookbookSelectWrapper/CookbookSelectWrapper';

const DEBOUNCE_SEARCH_TIMING = 200;

export interface UseServerSelectSearch {
  displayedOptions: DropdownOptionProps[];
  isInitialLoading: boolean;
  isFetching: boolean;
  onSearchHandle: (searchQuery: string) => void;
  restoreOptions: () => Promise<void>;
}

/**
 * Custom hook for server-side select search logic
 * Handles debounced search, loading states, and options management
 */
export function useServerSelectSearch(
  fetchOptions: (searchQuery?: string) => Promise<void>,
  options?: DropdownOptionProps[]
): UseServerSelectSearch {
  const [displayedOptions, setDisplayedOptions] = useState<
    DropdownOptionProps[]
  >([]);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Create debounced search handler
  const onSearchHandle = useMemo(
    () =>
      debounce(async (searchQuery: string) => {
        setSearchQuery(searchQuery);
        setIsFetching(true);
        try {
          await fetchOptions(searchQuery);
        } finally {
          setIsFetching(false);
        }
      }, DEBOUNCE_SEARCH_TIMING),
    [fetchOptions]
  );

  const handleInitialLoad = useCallback(async (): Promise<void> => {
    setIsInitialLoading(true);
    try {
      await fetchOptions();
    } finally {
      setIsInitialLoading(false);
    }
  }, [fetchOptions]);

  const restoreOptions = async (): Promise<void> => {
    // Only restore options if there was a search query
    if (!searchQuery) {
      return;
    }

    setSearchQuery('');
    setIsFetching(true);
    try {
      await fetchOptions();
    } finally {
      setIsFetching(false);
    }
  };

  // Initial load
  useEffect(() => {
    handleInitialLoad();
  }, [handleInitialLoad]);

  // Update displayed options when new options come in
  useEffect(() => {
    if (isFetching) {
      return;
    }
    setDisplayedOptions(options || []);
  }, [options, isFetching]);

  return {
    displayedOptions,
    isInitialLoading,
    isFetching,
    onSearchHandle,
    restoreOptions,
  };
}
